import { TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShellSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { useOrganizationTimeZone } from '@/hooks/use-organizations';
import { useOrganizationSwitcher } from '@/hooks/use-organization-switcher';
import { useSignoutMutation } from '@/modules/auth/hooks/use-auth';
import { useAuthStore } from '@/state/store/auth';

/**
 * Shown instead of the app when the organization has no time zone. The layout (and its organization
 * switcher) sits behind the gate, so the way out is offered here: another organization, or log out.
 */
const MissingTimeZone = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const { mutateAsync: signout } = useSignoutMutation();
  const { availableOrganizations, selectedOrganization, handleOrganizationSelect, isSwitching } =
    useOrganizationSwitcher();
  const otherOrganizations = availableOrganizations.filter(
    (org) => org.itemId !== selectedOrganization?.itemId
  );

  const handleLogout = async () => {
    try {
      await signout();
    } finally {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <div className="flex w-full max-w-md flex-col gap-4 rounded-lg border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-2 text-destructive">
          <TriangleAlert className="h-5 w-5 shrink-0" />
          <h1 className="text-base font-semibold">{t('ORGANIZATION_TIME_ZONE_MISSING')}</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('ORGANIZATION_TIME_ZONE_MISSING_DESCRIPTION')}
        </p>
        {otherOrganizations.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t('SWITCH_ORGANIZATION')}
            </span>
            {otherOrganizations.map((org) => (
              <Button
                key={org.itemId}
                variant="outline"
                className="justify-start"
                disabled={isSwitching}
                onClick={() => void handleOrganizationSelect(org.itemId)}
              >
                {org.name}
              </Button>
            ))}
          </div>
        )}
        <Button variant="ghost" onClick={() => void handleLogout()}>
          {t('LOG_OUT')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Holds the signed-in app back until the organization's time zone is known, so no form is filled or saved
 * with days read in another zone (see `org-time-zone.ts`). Without one the app is not shown at all.
 */
export const OrganizationTimeZoneGate = ({ children }: { children: React.ReactNode }) => {
  const { isResolving, isMissing } = useOrganizationTimeZone();

  if (isResolving) return <AppShellSkeleton />;
  if (isMissing) return <MissingTimeZone />;

  return <>{children}</>;
};
